import React from 'react';
import { Grid, Paper, Typography, Box, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Helper function to get frequency multiplier
const getFrequencyMultiplier = (frequency) => {
  switch (frequency) {
    case 'daily':
      return 365/12; // Monthly equivalent
    case 'weekly':
      return 52/12; // Monthly equivalent
    case 'fortnightly':
      return 26/12; // Monthly equivalent
    case 'monthly':
      return 1;
    case 'quarterly':
      return 1/3;
    case 'annually':
      return 1/12;
    case 'one-time':
      return 1/12; // Spread one-time transactions over a year
    default:
      return 1;
  }
};

const DashboardCharts = ({ balanceSheetItems, cashFlowItems }) => {
  const theme = useTheme();

  // Calculate asset and liability totals
  const assetTotal = balanceSheetItems
    .filter(item => item.type === 'asset')
    .reduce((sum, item) => sum + item.amount, 0);

  const liabilityTotal = balanceSheetItems
    .filter(item => item.type === 'liability')
    .reduce((sum, item) => sum + item.amount, 0);

  // Prepare data for asset/liability pie chart
  const pieChartData = {
    labels: ['Assets', 'Liabilities'],
    datasets: [
      {
        data: [assetTotal, liabilityTotal],
        backgroundColor: ['#4CAF50', '#F44336'],
        borderColor: ['#388E3C', '#D32F2F'],
        borderWidth: 1,
      },
    ],
  };

  // Group cash flow items by month
  const monthlyData = {};
  cashFlowItems.forEach(item => {
    const date = new Date(item.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = { income: 0, expenses: 0 };
    }

    const amount = item.amount * getFrequencyMultiplier(item.frequency);
    if (item.type === 'income') {
      monthlyData[monthKey].income += amount;
    } else {
      monthlyData[monthKey].expenses += amount;
    }
  });

  // Sort months chronologically
  const sortedMonths = Object.keys(monthlyData).sort();

  // Prepare data for income/expense bar chart
  const barChartData = {
    labels: sortedMonths.map(month => {
      const [year, monthNum] = month.split('-');
      return `${monthNum}/${year}`;
    }),
    datasets: [
      {
        label: 'Income',
        data: sortedMonths.map(month => monthlyData[month].income),
        backgroundColor: '#4CAF50',
        borderColor: '#388E3C',
        borderWidth: 1,
      },
      {
        label: 'Expenses',
        data: sortedMonths.map(month => monthlyData[month].expenses),
        backgroundColor: '#F44336',
        borderColor: '#D32F2F',
        borderWidth: 1,
      },
    ],
  };

  // Calculate net worth over time
  const netWorthData = sortedMonths.map(month => {
    const monthAssets = balanceSheetItems
      .filter(item => {
        const itemDate = new Date(item.date);
        const itemMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
        return item.type === 'asset' && itemMonth <= month;
      })
      .reduce((sum, item) => sum + item.amount, 0);

    const monthLiabilities = balanceSheetItems
      .filter(item => {
        const itemDate = new Date(item.date);
        const itemMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
        return item.type === 'liability' && itemMonth <= month;
      })
      .reduce((sum, item) => sum + item.amount, 0);

    return monthAssets - monthLiabilities;
  });

  // Prepare data for net worth line chart
  const lineChartData = {
    labels: sortedMonths.map(month => {
      const [year, monthNum] = month.split('-');
      return `${monthNum}/${year}`;
    }),
    datasets: [
      {
        label: 'Net Worth',
        data: netWorthData,
        fill: false,
        borderColor: '#2196F3',
        backgroundColor: '#2196F3',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary
        }
      },
    },
  };

  const pieChartOptions = {
    ...chartOptions,
    aspectRatio: 1,
    maintainAspectRatio: true,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        position: 'top',
        align: 'center',
        labels: {
          color: theme.palette.text.primary
        }
      },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ 
          p: 3, 
          height: '400px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          boxShadow: 2,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Assets vs Liabilities
          </Typography>
          <Box sx={{ 
            flex: 1,
            position: 'relative',
            width: '100%',
            height: '85%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Pie data={pieChartData} options={pieChartOptions} />
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper sx={{ 
          p: 3, 
          height: '400px',
          borderRadius: 3,
          boxShadow: 2,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Monthly Income vs Expenses
          </Typography>
          <Bar data={barChartData} options={chartOptions} />
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper sx={{ 
          p: 3, 
          height: '400px',
          borderRadius: 3,
          boxShadow: 2,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Net Worth Over Time
          </Typography>
          <Line data={lineChartData} options={chartOptions} />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DashboardCharts; 